/**
 * CasePortfolio - Attorney multi-case overview
 * Shows all active cases with strength indicators, filterable by status/value/urgency
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scale,
  DollarSign,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader2,
  ChevronRight,
  Filter,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface CaseItem {
  id: string;
  client_name: string;
  business_name?: string;
  event_type?: string;
  total_demand?: number;
  released_at: string;
  status: string;
  case_strength?: number;
  readiness_score?: number;
}

type StrengthLevel = 'strong' | 'moderate' | 'weak';
type StatusFilter = 'all' | 'strong' | 'moderate' | 'weak';

interface CasePortfolioProps {
  cases?: CaseItem[];
  className?: string;
}

function getStrength(score?: number): StrengthLevel {
  if (!score || score < 40) return 'weak';
  if (score < 70) return 'moderate';
  return 'strong';
}

const STRENGTH_STYLES = {
  strong: { bg: 'bg-green-100', text: 'text-green-700', icon: Shield, label: 'Strong' },
  moderate: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertTriangle, label: 'Moderate' },
  weak: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle, label: 'Weak' },
};

export default function CasePortfolio({ cases: propCases, className = '' }: CasePortfolioProps) {
  const navigate = useNavigate();
  const [cases, setCases] = useState<CaseItem[]>(propCases || []);
  const [loading, setLoading] = useState(!propCases);
  const [filter, setFilter] = useState<StatusFilter>('all');

  useEffect(() => {
    if (propCases) {
      setCases(propCases);
      return;
    }
    fetchCases();
  }, [propCases]);

  const fetchCases = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('attorney_token');
      const res = await fetch(`${BACKEND_URL}/api/v1/attorney/dashboard`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setCases(data.cases || []);
      }
    } catch (err) {
      console.warn('Failed to fetch portfolio cases:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = cases.filter((c) => {
    if (filter === 'all') return true;
    return getStrength(c.case_strength) === filter;
  });

  const counts = {
    strong: cases.filter((c) => getStrength(c.case_strength) === 'strong').length,
    moderate: cases.filter((c) => getStrength(c.case_strength) === 'moderate').length,
    weak: cases.filter((c) => getStrength(c.case_strength) === 'weak').length,
  };

  const totalValue = cases.reduce((s, c) => s + (c.total_demand || 0), 0);

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  if (loading) {
    return (
      <div className={`bg-white rounded-xl border border-slate-200 p-6 ${className}`}>
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading portfolio...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-slate-900">Case Portfolio</h3>
          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{cases.length} cases</span>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold text-green-600">{fmt(totalValue)}</p>
          <p className="text-xs text-slate-400">Total portfolio</p>
        </div>
      </div>

      {/* Strength Filters */}
      <div className="px-5 py-3 border-b border-slate-50 flex gap-2">
        {(['all', 'strong', 'moderate', 'weak'] as StatusFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              filter === f
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {f === 'all' ? `All (${cases.length})` : `${f.charAt(0).toUpperCase() + f.slice(1)} (${counts[f]})`}
          </button>
        ))}
      </div>

      {/* Case List */}
      <div className="divide-y divide-slate-50 max-h-[400px] overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No cases match this filter</div>
        ) : (
          filtered.map((c) => {
            const strength = getStrength(c.case_strength);
            const style = STRENGTH_STYLES[strength];
            const Icon = style.icon;
            return (
              <div
                key={c.id}
                onClick={() => navigate(`/attorney/case/${c.id}`)}
                className="px-5 py-3 flex items-center gap-4 hover:bg-slate-50 cursor-pointer transition-colors group"
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${style.bg}`}>
                  <Icon className={`w-4 h-4 ${style.text}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 text-sm truncate">{c.client_name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {c.event_type && (
                      <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{c.event_type}</span>
                    )}
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${style.bg} ${style.text}`}>
                      {style.label}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-slate-900">{fmt(c.total_demand || 0)}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500" />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
